import Gooey, {
    calc,
    collection,
    Component,
    field,
    model,
} from '@srhazi/gooey';

import { KeyboardKey } from './KeyboardKey';

const rows = [
    'asdfghjkl;1234'.split(''),
    'zxcvbnm,./qwer'.split(''),
] as (keyof typeof keyToCode)[][];

const numRows = rows.length;
const numCols = rows[0].length;

const keyToCode = {
    '1': 'Digit1',
    '2': 'Digit2',
    '3': 'Digit3',
    '4': 'Digit4',
    '5': 'Digit5',
    '6': 'Digit6',
    '7': 'Digit7',
    '8': 'Digit8',
    '9': 'Digit9',
    '0': 'Digit0',
    '-': 'Minus',
    '=': 'Equal',
    a: 'KeyA',
    b: 'KeyB',
    c: 'KeyC',
    d: 'KeyD',
    e: 'KeyE',
    f: 'KeyF',
    g: 'KeyG',
    h: 'KeyH',
    i: 'KeyI',
    j: 'KeyJ',
    k: 'KeyK',
    l: 'KeyL',
    m: 'KeyM',
    n: 'KeyN',
    o: 'KeyO',
    p: 'KeyP',
    q: 'KeyQ',
    r: 'KeyR',
    s: 'KeyS',
    t: 'KeyT',
    u: 'KeyU',
    v: 'KeyV',
    w: 'KeyW',
    x: 'KeyX',
    y: 'KeyY',
    z: 'KeyZ',
    ';': 'Semicolon',
    "'": 'Quote',
    ',': 'Comma',
    '.': 'Period',
    '/': 'Slash',
};
const codeToKey: Record<string, keyof typeof keyToCode> = {
    Digit1: '1',
    Digit2: '2',
    Digit3: '3',
    Digit4: '4',
    Digit5: '5',
    Digit6: '6',
    Digit7: '7',
    Digit8: '8',
    Digit9: '9',
    Digit0: '0',
    Minus: '-',
    Equal: '=',
    KeyA: 'a',
    KeyB: 'b',
    KeyC: 'c',
    KeyD: 'd',
    KeyE: 'e',
    KeyF: 'f',
    KeyG: 'g',
    KeyH: 'h',
    KeyI: 'i',
    KeyJ: 'j',
    KeyK: 'k',
    KeyL: 'l',
    KeyM: 'm',
    KeyN: 'n',
    KeyO: 'o',
    KeyP: 'p',
    KeyQ: 'q',
    KeyR: 'r',
    KeyS: 's',
    KeyT: 't',
    KeyU: 'u',
    KeyV: 'v',
    KeyW: 'w',
    KeyX: 'x',
    KeyY: 'y',
    KeyZ: 'z',
    Semicolon: ';',
    Quote: "'",
    Comma: ',',
    Period: '.',
    Slash: '/',
};

const getFrequency = function (baseFreq: number, numSemitones: number) {
    return baseFreq * Math.pow(Math.pow(2, 1 / 24), numSemitones);
};

const keyFrequency = {
    '1': getFrequency(440, 19),
    '2': getFrequency(440, 21),
    '3': getFrequency(440, 23),
    '4': getFrequency(440, 25),
    '5': getFrequency(0, 7),
    '6': getFrequency(0, 9),
    '7': getFrequency(0, 11),
    '8': getFrequency(0, 12),
    '9': getFrequency(0, 14),
    '0': getFrequency(0, 16),
    '-': getFrequency(0, 17),
    '=': getFrequency(0, 19),

    q: getFrequency(440, 20),
    w: getFrequency(440, 22),
    e: getFrequency(440, 24),
    r: getFrequency(440, 26),
    t: getFrequency(0, 7),
    y: getFrequency(0, 9),
    u: getFrequency(0, 11),
    i: getFrequency(0, 12),
    o: getFrequency(0, 14),
    p: getFrequency(0, 16),

    a: getFrequency(440, -1),
    s: getFrequency(440, 1),
    d: getFrequency(440, 3),
    f: getFrequency(440, 5),
    g: getFrequency(440, 7),
    h: getFrequency(440, 9),
    j: getFrequency(440, 11),
    k: getFrequency(440, 13),
    l: getFrequency(440, 15),
    ';': getFrequency(440, 17),

    z: getFrequency(440, 0),
    x: getFrequency(440, 2),
    c: getFrequency(440, 4),
    v: getFrequency(440, 6),
    b: getFrequency(440, 8),
    n: getFrequency(440, 10),
    m: getFrequency(440, 12),
    ',': getFrequency(440, 14),
    '.': getFrequency(440, 16),
    '/': getFrequency(440, 18),
};

type Mode =
    | 'ionian'
    | 'aeolian'
    | 'lydian'
    | 'mixolydian'
    | 'dorian'
    | 'phrygian'
    | 'locrian'
    | 'chromatic'
    | 'harmonic minor'
    | 'double harmonic minor'
    | 'pentatonic major'
    | 'pentatonic suspended'
    | 'pentatonic blues minor'
    | 'pentatonic blues major'
    | 'pentatonic minor'
    | '24tet'
    | 'bayati'
    | 'rast ascending'
    | 'rast descending'
    | 'rast both'
    | 'saba'
    | 'sigah';

const modes: Record<Mode, { label: string; notes: string }> = {
    locrian: { label: 'Locrian (VII)', notes: 'zxvnm.qe' },
    phrygian: { label: 'Phrygian (III)', notes: 'zxvn,.qe' },
    aeolian: { label: 'Aeolian [Minor] (VI)', notes: 'zcvn,.qe' },
    dorian: { label: 'Dorian [Scarborogh Fair] (II)', notes: 'zcvn,/qe' },
    mixolydian: { label: 'Mixolydian [Happy Birthday] (V)', notes: 'zcbn,/qe' },
    ionian: { label: 'Ionian [Major] (I)', notes: 'zcbn,/we' },
    lydian: { label: 'Lydian [Simpsons Theme] (IV)', notes: 'zcbm,/we' },
    'harmonic minor': {
        label: 'Harmonic Minor',
        notes: 'zcvn,.we',
    },
    'double harmonic minor': {
        label: 'Double Harmonic Minor',
        notes: 'zxbn,.we',
    },
    chromatic: {
        label: 'Chromatic (12 subdivisions)',
        notes: 'zxcvbnm,./qwer',
    },
    'pentatonic major': {
        label: 'Pentatonic (major) [宮 gōng]',
        notes: 'zcb,/e',
    },
    'pentatonic suspended': {
        label: 'Pentatonic (suspended) [商 shāng]',
        notes: 'zcn,qe',
    },
    'pentatonic blues minor': {
        label: 'Pentatonic (blues minor) [角 jué]',
        notes: 'zvn.qe',
    },
    'pentatonic blues major': {
        label: 'Pentatonic (blues major) [徵 zhǐ]',
        notes: 'zcn,/e',
    },
    'pentatonic minor': {
        label: 'Pentatonic (minor) [羽 yǔ]',
        notes: 'zvn,qe',
    },
    '24tet': {
        label: '24-TET (all 24 subdivisions)',
        notes: 'azsxdcfvgbhnjmk,l.;/1q2w3e4r',
    },
    bayati: { label: 'Bayati (بیاتی)', notes: 'zdvn,.qe' },
    'rast ascending': { label: 'Rast (راست) (ascending)', notes: 'zcgn,/2e' },
    'rast descending': { label: 'Rast (راست) (descending)', notes: 'zcgn,/qe' },
    'rast both': { label: 'Rast (راست) (both)', notes: 'zcgn,/q2e' },
    saba: { label: 'Saba (صبا)', notes: 'zdvb,.qe' },
    sigah: { label: 'Sigah (سه گاه)', notes: 'svbmlqe4' },
};
const westernModes: Mode[] = [
    'locrian',
    'phrygian',
    'aeolian',
    'dorian',
    'mixolydian',
    'ionian',
    'lydian',
];
const westernMiscModes: Mode[] = [
    'chromatic',
    'harmonic minor',
    'double harmonic minor',
];
const pentatonicModes: Mode[] = [
    'pentatonic major',
    'pentatonic suspended',
    'pentatonic blues major',
    'pentatonic blues minor',
    'pentatonic minor',
];
const maqamModes: Mode[] = [
    '24tet',
    'bayati',
    'rast ascending',
    'rast descending',
    'rast both',
    'saba',
    'sigah',
];

export const Keyboard: Component = (props, { onMount }) => {
    const mode = field<keyof typeof modes>('ionian');
    const gainValue = field(0.15);
    const waveform = field<OscillatorType>('triangle');
    const audioContext = new AudioContext({
        latencyHint: 'interactive',
    });
    const shiftKey = field(false);
    const gain = audioContext.createGain();
    gain.gain.value = gainValue.get();
    gain.connect(audioContext.destination);
    const oscillators = Object.fromEntries(
        Object.entries(keyFrequency).map(([key, freq]) => {
            const oscillatorGain = audioContext.createGain();
            oscillatorGain.gain.value = 0;
            oscillatorGain.connect(gain);
            const oscillator = audioContext.createOscillator();
            oscillator.type = waveform.get();
            oscillator.frequency.value = freq;
            oscillator.connect(oscillatorGain);
            oscillator.start(audioContext.currentTime);
            return [key, { oscillator, oscillatorGain }];
        })
    );

    const pressedState = model({
        '1': false,
        '2': false,
        '3': false,
        '4': false,
        '5': false,
        '6': false,
        '7': false,
        '8': false,
        '9': false,
        '0': false,
        '-': false,
        '=': false,
        a: false,
        b: false,
        c: false,
        d: false,
        e: false,
        f: false,
        g: false,
        h: false,
        i: false,
        j: false,
        k: false,
        l: false,
        m: false,
        n: false,
        o: false,
        p: false,
        q: false,
        r: false,
        s: false,
        t: false,
        u: false,
        v: false,
        w: false,
        x: false,
        y: false,
        z: false,
        ';': false,
        "'": false,
        ',': false,
        '.': false,
        '/': false,
    });
    const timings = collection<number>([]);
    let lastTime: number | null = null;
    let lastDelta: number | null = null;
    const exponentialMovingAverage = field<null | number>(null);

    const onKeyDown = (note: keyof typeof keyToCode) => {
        const now = performance.now();
        const currentTime = audioContext.currentTime;
        if (oscillators[note]) {
            oscillators[note].oscillatorGain.gain.cancelScheduledValues(
                currentTime
            );
            oscillators[note].oscillatorGain.gain.linearRampToValueAtTime(
                1,
                currentTime + 0.05
            );
        }
        pressedState[note] = true;
    };
    const onKeyUp = (note: keyof typeof keyToCode) => {
        const currentTime = audioContext.currentTime;
        if (oscillators[note]) {
            oscillators[note].oscillatorGain.gain.cancelScheduledValues(
                currentTime
            );
            oscillators[note].oscillatorGain.gain.linearRampToValueAtTime(
                0,
                currentTime + 0.5
            );
        }
        pressedState[note] = false;
    };

    onMount(() => {
        document.body.addEventListener('keydown', (e) => {
            if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') {
                shiftKey.set(true);
            }
            if (e.repeat) {
                return;
            }
            const currentMode = modes[mode.get()];
            if (e.shiftKey && '1234567890'.includes(e.code.slice(-1))) {
                const index = parseInt(e.code.slice(-1)) - 1;
                if (currentMode.notes[index]) {
                    onKeyDown(
                        currentMode.notes[index] as keyof typeof keyToCode
                    );
                }
            } else if (
                e.code in codeToKey &&
                codeToKey[e.code] &&
                currentMode.notes.includes(codeToKey[e.code])
            ) {
                onKeyDown(codeToKey[e.code]);
            }
        });
        document.body.addEventListener('keyup', (e) => {
            if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') {
                shiftKey.set(false);
            }
            const currentMode = modes[mode.get()];
            if (e.shiftKey && '1234567890'.includes(e.code.slice(-1))) {
                const index = parseInt(e.code.slice(-1)) - 1;
                if (currentMode.notes[index]) {
                    onKeyUp(currentMode.notes[index] as keyof typeof keyToCode);
                }
            } else if (
                e.code in codeToKey &&
                codeToKey[e.code] &&
                currentMode.notes.includes(codeToKey[e.code])
            ) {
                onKeyUp(codeToKey[e.code]);
            }
        });
        const gainSubscription = gainValue.subscribe((err, val) => {
            if (err) return;
            gain.gain.value = val;
        });
        const waveformSubscription = waveform.subscribe((err, val) => {
            if (err) return;
            Object.values(oscillators).forEach((osc) => {
                osc.oscillator.type = val;
            });
        });
    });

    return (
        <div>
            <fieldset style:display="flex" style:gap="20px">
                <legend>Mode</legend>
                <div>
                    <p>Standard western diatonic modes:</p>
                    {westernModes.map((name) => {
                        const modeValue = modes[name];
                        return (
                            <div>
                                <label>
                                    <input
                                        type="radio"
                                        name="mode"
                                        checked={calc(
                                            () => mode.get() === name
                                        )}
                                        value={name as Mode}
                                        on:input={(e, el) => {
                                            if (el.checked) {
                                                mode.set(el.value as Mode);
                                            }
                                        }}
                                    />{' '}
                                    {modeValue.label}
                                </label>
                            </div>
                        );
                    })}
                </div>
                <div>
                    <p>Misc western modes:</p>
                    {westernMiscModes.map((name) => {
                        const modeValue = modes[name];
                        return (
                            <div>
                                <label>
                                    <input
                                        type="radio"
                                        name="mode"
                                        checked={calc(
                                            () => mode.get() === name
                                        )}
                                        value={name as Mode}
                                        on:input={(e, el) => {
                                            if (el.checked) {
                                                mode.set(el.value as Mode);
                                            }
                                        }}
                                    />{' '}
                                    {modeValue.label}
                                </label>
                            </div>
                        );
                    })}
                </div>
                <div>
                    <p>Pentatonic modes:</p>
                    {pentatonicModes.map((name) => {
                        const modeValue = modes[name];
                        return (
                            <div>
                                <label>
                                    <input
                                        type="radio"
                                        name="mode"
                                        checked={calc(
                                            () => mode.get() === name
                                        )}
                                        value={name as Mode}
                                        on:input={(e, el) => {
                                            if (el.checked) {
                                                mode.set(el.value as Mode);
                                            }
                                        }}
                                    />{' '}
                                    {modeValue.label}
                                </label>
                            </div>
                        );
                    })}
                </div>
                <div>
                    <p>
                        Middle eastern (maqamat) modes (note: this is{' '}
                        <em>very</em> incomplete, there are 72 modes in
                        maqamat):
                    </p>
                    {maqamModes.map((name) => {
                        const modeValue = modes[name];
                        return (
                            <div>
                                <label>
                                    <input
                                        type="radio"
                                        name="mode"
                                        checked={calc(
                                            () => mode.get() === name
                                        )}
                                        value={name as Mode}
                                        on:input={(e, el) => {
                                            if (el.checked) {
                                                mode.set(el.value as Mode);
                                            }
                                        }}
                                    />{' '}
                                    {modeValue.label}
                                </label>
                            </div>
                        );
                    })}
                </div>
            </fieldset>
            <fieldset
                style:display="flex"
                style:gap="20px"
                style:align-items="flex-start"
            >
                <legend>Controls</legend>
                <div>
                    <label>
                        <p>Gain:</p>
                        <input
                            type="range"
                            name="gain"
                            value={calc(() => gainValue.get() * 100)}
                            min="0"
                            max="100"
                            on:input={(e, el) => {
                                gainValue.set(el.valueAsNumber / 100);
                            }}
                        />
                    </label>
                </div>
                <div>
                    <p>Oscillator:</p>
                    {(['sawtooth', 'sine', 'square', 'triangle'] as const).map(
                        (type: OscillatorType) => (
                            <label>
                                <input
                                    type="radio"
                                    name="waveform"
                                    value={type}
                                    checked={calc(
                                        () => waveform.get() === type
                                    )}
                                    on:input={(e, el) => {
                                        if (el.checked) {
                                            waveform.set(type);
                                        }
                                    }}
                                />{' '}
                                {type}
                            </label>
                        )
                    )}
                </div>
                <div></div>
            </fieldset>
            <fieldset>
                <legend>Notes</legend>
                <p>
                    Keys correspond to notes; hold shift to map numbers to the
                    active keys per the selected mode (note: doesn't work well
                    with chromatic/24tet).
                </p>
                <div>
                    {rows.map((row, rowIndex) => (
                        <div style:padding-left={rowIndex === 1 ? '25px' : '0'}>
                            {row.map((key, keyIndex) => (
                                <KeyboardKey
                                    active={calc(() =>
                                        modes[mode.get()].notes.includes(key)
                                    )}
                                    hue={`${(360 * keyIndex) / numCols}deg`}
                                    saturation={`${100 * (0.25 + (0.5 * rowIndex) / numRows)}%`}
                                    key={key}
                                    label={calc(() =>
                                        shiftKey.get()
                                            ? modes[mode.get()].notes.includes(
                                                  key
                                              )
                                                ? (
                                                      modes[
                                                          mode.get()
                                                      ].notes.indexOf(key) + 1
                                                  ).toString()
                                                : '-'
                                            : key
                                    )}
                                    isPressed={calc(() => pressedState[key])}
                                    onMouseDown={() => onKeyDown(key)}
                                    onMouseUp={() => onKeyUp(key)}
                                />
                            ))}
                        </div>
                    ))}
                </div>
            </fieldset>
        </div>
    );
};
